import { ApplicationConfig, enableProdMode, importProvidersFrom, inject, Injector, LOCALE_ID, provideAppInitializer } from '@angular/core';
import { environment } from './environments/environment';
import { AppConfigurationModel } from './app/app-configuration-model';
import { APP_CONFIG } from './app/app-config.service';
import { registerLocaleData } from '@angular/common';
import { LocalizationService } from './app/shared/localization/localization.service';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { MessageService } from 'primeng/api';
import { UserGuard } from './app/admin/user-guard';
import { AuthHttpInterceptor } from './app/auth/auth-http-interceptor';
import { AuthGuard } from './app/auth/auth.guard';
import { UnauthorizedHttpInterceptor } from './app/auth/unauthorized-http-interceptor';
import { KreditoruiVersionInterceptor } from './app/shared/interceptors/kreditorui-version.interceptor';
import { routes } from './app/app-routes';
import { providePrimeNG } from 'primeng/config';
import localeNb from '@angular/common/locales/nb';
import localeDanish from '@angular/common/locales/da';
import { MissingTranslationHandler, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiTranslateLoader } from './app/shared/localization/api-localization-loader';
import { MyMissingTranslationHandler } from './app/shared/localization/missing-translation.handler';
import { KreditorService } from './app/shared/kreditor.service';
import Aura from '@primeng/themes/aura';
import { TimezoneHttpInterceptor } from './app/shared/interceptors/timezone-interceptor';

if (environment.production) {
  enableProdMode();
}

export const applicationConfig: ApplicationConfig = {
  providers: [
    MessageService,
    AuthGuard,
    UserGuard,
    provideAnimations(),
    provideRouter(routes),
    importProvidersFrom(FormsModule, ReactiveFormsModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: ApiTranslateLoader,
          deps: [KreditorService]
        },
        missingTranslationHandler: {
          provide: MissingTranslationHandler,
          useClass: MyMissingTranslationHandler
        }
      })),
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(() => initializeApp(inject(TranslateService), inject(Injector), inject(LocalizationService))),
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: UnauthorizedHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: KreditoruiVersionInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TimezoneHttpInterceptor, multi: true },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { ...new MatDialogConfig(), panelClass: 'popup' } as MatDialogConfig },
    { provide: LOCALE_ID, useValue: 'nb-NO' },
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          cssLayer: {
            name: 'primeng',
            order: 'reset, bootstrap, primeng, theme'
          },
          darkModeSelector: false
        }
      }
    }),
  ]
}

export function initializeApp(translateService: TranslateService, injector: Injector, localizationService: LocalizationService) {
  return new Promise<void>((resolve) => {
    const appConfigModel = injector.get(APP_CONFIG) as AppConfigurationModel;
    const configLanguage = appConfigModel.languageCode.toLowerCase();
    const supported = appConfigModel.supportedLanguages.map(lng => lng.code);
    translateService.addLangs(supported);

    const sessionLanguage = localStorage.getItem('language') ?? configLanguage;
    const languageChangeResult = localizationService.changeLanguage(sessionLanguage);

    if (languageChangeResult instanceof Promise) {
      languageChangeResult.then(() => resolve());
    } else if (languageChangeResult.subscribe) {
      languageChangeResult.subscribe(() => resolve());
    } else {
      resolve();
    }
  });
}

fetch('environments/appConfig.json')
  .then(response => response.json())
  .then((config: AppConfigurationModel) => {
    applicationConfig.providers.push({
      provide: APP_CONFIG,
      useValue: config,
      deps: [HttpClient, APP_CONFIG]
    });

    bootstrapApplication(AppComponent, applicationConfig)
      .catch(() => { });
  })
  .catch(err => console.error('[INIT] Failed to load appConfig.json:', err));

registerLocaleData(localeNb, 'nb-NO');
registerLocaleData(localeDanish, 'da-DK');
registerLocaleData(localeDanish, 'fi-FI');

